// Shared bits for the Workers surface (the background workers deployed with ff-api).
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import {
  Ban,
  Eye,
  Gauge,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
  RefreshCw,
  RotateCw,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  WorkerChange,
  useWorkerAckMutation,
  useWorkerChangeMutation,
  workersKeys,
} from '@/client/api/workers/workers.query';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorCode } from '@/client/utils/errors';
import {
  Failure,
  FailureGroup,
  Figures,
  HeldTask,
  Points,
  Run,
  ScheduleShape,
  SeriesPoint,
  Worker,
  WorkerAuditRow,
  WorkerEnv,
  WorkerIssue,
  WorkerWindow,
} from '@/client/types/workers';
import {
  Card,
  EmptyRow,
  FormDialog,
  FormField,
  HEALTH,
  HealthPill,
  INPUT_CLASS,
  ReasonDialog,
  RunsChart,
  Th,
  cloudErrorText,
  fmtAgo,
  fmtNum,
  fmtTime,
} from '../Cloud/cloudUi';

export const WORKERS_BASE = '/admin/workers';
export const ENVS: readonly WorkerEnv[] = ['production', 'staging'];
export const WINDOWS: readonly WorkerWindow[] = ['6h', '24h', '7d', '30d'];

/* ---------- which environment ---------- */

/** The environment lives in the URL, so a link to a worker lands on the right broker. */
export const useWorkerEnv = (): [WorkerEnv, (env: WorkerEnv) => void] => {
  const [params, setParams] = useSearchParams();
  const env: WorkerEnv = params.get('env') === 'staging' ? 'staging' : 'production';
  return [
    env,
    (next) => {
      const out = new URLSearchParams(params);
      if (next === 'production') out.delete('env');
      else out.set('env', next);
      setParams(out, { replace: true });
    },
  ];
};

const withEnv = (path: string, env: WorkerEnv) =>
  env === 'staging' ? `${path}?env=staging` : path;

export const workerPath = (name: string, env: WorkerEnv) =>
  withEnv(`${WORKERS_BASE}/fleet/${encodeURIComponent(name)}`, env);
export const queuePath = (name: string, env: WorkerEnv) =>
  withEnv(`${WORKERS_BASE}/queues/${encodeURIComponent(name)}`, env);
export const taskPath = (name: string, env: WorkerEnv) =>
  withEnv(`${WORKERS_BASE}/tasks/${encodeURIComponent(name)}`, env);
export const tabPath = (tab: string, env: WorkerEnv) =>
  withEnv(tab === 'overview' ? WORKERS_BASE : `${WORKERS_BASE}/${tab}`, env);

const LINK = 'font-medium text-gray-900 hover:underline';

/** `tasks.billing_tasks.renew` reads as `renew`, with its module underneath. */
export const shortTask = (name?: string | null) => (name ? name.split('.').pop()! : 'unknown');
export const taskModule = (name?: string | null) =>
  name && name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : '';
export const shortWorker = (name: string) => name.replace(/^celery@/, '');

export const WorkerLink: React.FC<{ name?: string | null; env: WorkerEnv }> = ({ name, env }) =>
  name ? (
    <Link to={workerPath(name, env)} className={LINK}>
      {shortWorker(name)}
    </Link>
  ) : (
    <span className="text-gray-400">not recorded</span>
  );

export const QueueLink: React.FC<{ name?: string | null; env: WorkerEnv }> = ({ name, env }) =>
  name ? (
    <Link to={queuePath(name, env)} className="font-mono text-xs text-gray-700 hover:underline">
      {name}
    </Link>
  ) : null;

export const TaskLink: React.FC<{ name?: string | null; env: WorkerEnv }> = ({ name, env }) =>
  name ? (
    <Link to={taskPath(name, env)} className="block min-w-0" title={name}>
      <span className={LINK}>{shortTask(name)}</span>
      <span className="block truncate font-mono text-[11px] text-gray-400">{taskModule(name)}</span>
    </Link>
  ) : (
    <span className="text-gray-400">unknown task</span>
  );

/* ---------- formatting ---------- */

export const fmtMs = (ms?: number | null) =>
  ms == null
    ? '—'
    : ms >= 60_000
      ? `${(ms / 60_000).toFixed(1)} min`
      : ms >= 1000
        ? `${(ms / 1000).toFixed(1)} s`
        : `${Math.round(ms)} ms`;

export const fmtSpan = (sec?: number | null) => {
  if (sec == null) return '—';
  if (sec < 90) return `${Math.round(sec)} s`;
  if (sec < 5400) return `${Math.round(sec / 60)} min`;
  if (sec < 172800) return `${(sec / 3600).toFixed(1)} h`;
  return `${Math.round(sec / 86400)} d`;
};

export const fmtFailRate = (f: Pick<Figures, 'runs' | 'fail_rate'>) =>
  !f.runs || f.fail_rate == null ? '—' : `${(f.fail_rate * 100).toFixed(f.fail_rate < 0.1 ? 1 : 0)}%`;

export const FailRate: React.FC<{ f: Pick<Figures, 'runs' | 'fail_rate' | 'failed'> }> = ({ f }) => (
  <span
    className={
      !f.failed
        ? 'text-gray-500'
        : (f.fail_rate ?? 0) >= 0.2
          ? 'font-medium text-red-700'
          : 'font-medium text-amber-700'
    }
    title={`${f.failed} failed of ${f.runs}`}
  >
    {fmtFailRate(f)}
  </span>
);

export const describeSchedule = (s: ScheduleShape): string => {
  if (s.kind === 'interval_seconds' && s.value) return `Every ${fmtSpan(s.value)}`;
  if (s.kind === 'crontab') {
    const rest = [s.day_of_week, s.day_of_month, s.month_of_year].every((v) => v === '*');
    return `cron ${s.minute} ${s.hour}${rest ? '' : ` ${s.day_of_month} ${s.month_of_year} ${s.day_of_week}`}`;
  }
  return 'Not in this code';
};

/* ---------- notices ---------- */

const ERROR_COPY: Record<string, string> = {
  broker_unreachable: 'The broker did not answer.',
  broker_not_redis: 'This environment does not use a Redis broker.',
  mongo_unavailable: 'The operations database is unavailable.',
  worker_not_answering: 'That worker is not answering.',
  task_not_allowed: 'Only tasks in the routing table can be run from here.',
  arguments_not_kept: 'The arguments of that run were too large to keep, so it cannot be sent again.',
  rate_limit_refused: 'No worker accepted the rate limit.',
  missing_reason: 'A reason is required.',
};

export const workersErrorText = (error: unknown): string => {
  const code = getApiErrorCode(error);
  return code && ERROR_COPY[code] ? ERROR_COPY[code] : cloudErrorText(error);
};

export const ErrorNotice: React.FC<{ error: unknown; onRetry?: () => void }> = ({
  error,
  onRetry,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
    <span>{workersErrorText(error)}</span>
    {onRetry && (
      <Button size="sm" variant="outline" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    )}
  </div>
);

export const AccessDenied: React.FC = () => (
  <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
    <p className="text-sm font-medium text-gray-800">You don&apos;t have access to Workers</p>
    <p className="mt-1 text-sm text-gray-500">
      Ask an administrator for the workers.view permission.
    </p>
  </div>
);

export const LoadingNote: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div
    role="status"
    className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800"
  >
    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
    <span>
      {children ??
        'Asking every worker what it is doing. They get a second and a half to answer.'}
    </span>
  </div>
);

export const FetchingChip: React.FC = () =>
  useIsFetching({ queryKey: workersKeys.all }) > 0 ? (
    <span
      role="status"
      className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
    >
      <Loader2 className="h-3 w-3 animate-spin" />
      Updating
    </span>
  ) : null;

/** Says why the figures are empty, when the reason is that nothing has recorded any yet. */
export const RecordingNote: React.FC<{ since?: number | null; env: WorkerEnv }> = ({
  since,
  env,
}) =>
  since === null ? (
    <p className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      No {env} worker has recorded a run yet. Run counts, runtimes, queue wait and failures start
      when the {env} workers are deployed with the recording change; live state, queues and the
      schedule do not depend on it.
    </p>
  ) : since ? (
    <p className="text-xs text-gray-500">Figures recorded since {fmtTime(since)}.</p>
  ) : null;

/** Whether the viewer may run things and change things. */
export const useWorkerPerms = () => {
  const view = useHasPermission('workers.view');
  const operate = useHasPermission('workers.operate');
  const manage = useHasPermission('workers.manage');
  const logs = useHasPermission('cloud.logs');
  return {
    loading: view.isLoading,
    canView: view.isSuperAdmin || view.hasPermission,
    canOperate: operate.isSuperAdmin || operate.hasPermission,
    canManage: manage.isSuperAdmin || manage.hasPermission,
    canLogs: logs.isSuperAdmin || logs.hasPermission,
  };
};

/* ---------- charts ---------- */

/** Runs and failures per bucket. */
export const Throughput: React.FC<{ series?: SeriesPoint[]; window: WorkerWindow }> = ({
  series,
  window,
}) => (
  <RunsChart
    series={(series ?? []).map((p) => ({ ...p, elapsed: 0 }))}
    window={window === '6h' ? '24h' : window}
  />
);

/** A trend in a table cell, scaled to its own peak. */
export const Spark: React.FC<{ points?: Points | null; label: string; unit?: string }> = ({
  points,
  label,
  unit = '',
}) => {
  if (!points || points.length < 2) return null;
  const w = 120;
  const h = 28;
  const t0 = points[0][0];
  const span = points[points.length - 1][0] - t0 || 1;
  const peak = Math.max(...points.map((p) => p[1]), 1);
  const path = points.map(
    ([t, v]) => `${(((t - t0) / span) * w).toFixed(1)},${(h - 1 - (v / peak) * (h - 2)).toFixed(1)}`,
  );
  return (
    <svg width={w} height={h} role="img" aria-label={`${label}, peak ${Math.round(peak)}${unit}`}>
      <title>{`${label}: peak ${Math.round(peak)}${unit}`}</title>
      <polyline points={path.join(' ')} fill="none" stroke="#2563eb" strokeWidth={1.5} />
    </svg>
  );
};

/** One figure per bucket as a sparkline-sized series for the cloud line chart. */
export const seriesOf = (
  series: SeriesPoint[] | undefined,
  key: 'runtime_ms' | 'wait_ms',
  name: string,
) => [
  {
    component: name,
    instance: name,
    points: (series ?? [])
      .filter((p) => p[key] != null)
      .map((p) => [p.at, Math.round(p[key] as number)] as [number, number]),
  },
];

/* ---------- changes: one dialog, one menu ---------- */

type ChangeCopy = {
  title: string;
  description: React.ReactNode;
  confirm: string;
  destructive?: boolean;
  typed?: string;
};

const copyFor = (c: WorkerChange, env: WorkerEnv): ChangeCopy => {
  switch (c.kind) {
    case 'run_task':
      return {
        title: `Run ${shortTask(c.task)} now`,
        description: `Sends ${c.task} to its queue on ${env}. It runs as soon as a worker is free.`,
        confirm: 'Run now',
      };
    case 'run_entry':
      return {
        title: `Dispatch ${c.entry} now`,
        description: `Sends this schedule entry's task on ${env} with the arguments the schedule gives it. Its next scheduled run is not moved.`,
        confirm: 'Dispatch',
      };
    case 'revoke':
      return {
        title: 'Drop this task',
        description:
          'Tells every worker to discard this task when it reaches them. A task that is already running keeps running: a thread cannot be stopped. Workers forget revokes when they restart.',
        confirm: 'Drop task',
        destructive: true,
      };
    case 'retry_failure':
      return {
        title: 'Send this run again',
        description: `Sends the task again on ${env} with the same arguments. Check that running it twice is safe.`,
        confirm: 'Send again',
      };
    case 'pause_queue':
      return {
        title: `Stop ${shortWorker(c.worker)} consuming ${c.queue}`,
        description:
          'The worker finishes what it holds and takes nothing more from this queue. Messages stay in the queue. The pause is lost when the worker restarts.',
        confirm: 'Pause',
        destructive: true,
      };
    case 'resume_queue':
      return {
        title: `Resume ${c.queue} on ${shortWorker(c.worker)}`,
        description: 'The worker starts taking messages from this queue again.',
        confirm: 'Resume',
      };
    case 'restart':
      return {
        title: `Restart ${shortWorker(c.worker)}`,
        description: `Restarts the ${c.component} component at the provider. Tasks it is running are lost, and pauses and rate limits set from here are cleared.`,
        confirm: 'Restart',
        destructive: true,
        typed: env === 'production' ? c.component : undefined,
      };
    case 'rate_limit':
      return {
        title: `Rate limit ${shortTask(c.task)}`,
        description:
          'Caps how often each worker starts this task. 0 lifts the limit. It is lost when a worker restarts.',
        confirm: 'Set limit',
      };
    case 'purge':
      return {
        title: `Purge ${c.queue}`,
        description: `Throws away every message waiting in this queue on ${env}. The work they asked for will not happen, and this cannot be undone.`,
        confirm: 'Purge queue',
        destructive: true,
        typed: c.queue,
      };
  }
};

const parseJson = <T,>(text: string, ok: (v: unknown) => boolean, empty: T): T | null => {
  if (!text.trim()) return empty;
  try {
    const value = JSON.parse(text);
    return ok(value) ? (value as T) : null;
  } catch {
    return null;
  }
};

/** Confirms one change with a reason, and asks for whatever else that change needs. */
export const ChangeDialog: React.FC<{
  env: WorkerEnv;
  change: WorkerChange;
  onClose: () => void;
}> = ({ env, change, onClose }) => {
  const mutation = useWorkerChangeMutation();
  const [args, setArgs] = useState('');
  const [kwargs, setKwargs] = useState('');
  const [rate, setRate] = useState(change.kind === 'rate_limit' ? change.rate : '');
  const [done, setDone] = useState<string | null>(null);
  const copy = copyFor(change, env);
  const parsedArgs = parseJson<unknown[]>(args, Array.isArray, []);
  const parsedKwargs = parseJson<Record<string, unknown>>(
    kwargs,
    (v) => !!v && typeof v === 'object' && !Array.isArray(v),
    {},
  );
  const valid =
    change.kind === 'run_task'
      ? parsedArgs !== null && parsedKwargs !== null
      : change.kind === 'rate_limit'
        ? /^\d{1,6}(\/[smh])?$/.test(rate)
        : true;
  if (done) {
    return (
      <ReasonDialog
        open
        onClose={onClose}
        title="Done"
        description={done}
        confirmLabel="Close"
        onConfirm={onClose}
      />
    );
  }
  return (
    <FormDialog
      title={copy.title}
      description={copy.description}
      confirmLabel={copy.confirm}
      destructive={copy.destructive}
      confirmText={copy.typed}
      valid={valid}
      busy={mutation.isPending}
      error={mutation.error}
      onClose={onClose}
      onConfirm={(reason) =>
        mutation.mutate(
          {
            env,
            reason,
            change:
              change.kind === 'run_task'
                ? { ...change, args: parsedArgs ?? [], kwargs: parsedKwargs ?? {} }
                : change.kind === 'rate_limit'
                  ? { ...change, rate }
                  : change,
          },
          {
            onSuccess: (r) =>
              r.task_id
                ? setDone(`Sent to ${r.queue ?? 'its queue'} as task ${r.task_id}.`)
                : r.removed != null
                  ? setDone(`${r.removed} message${r.removed === 1 ? '' : 's'} removed.`)
                  : onClose(),
          },
        )
      }
    >
      {change.kind === 'run_task' && (
        <>
          <FormField label="Arguments (a JSON list, optional)">
            <input
              value={args}
              onChange={(e) => setArgs(e.target.value)}
              placeholder="[]"
              className={`${INPUT_CLASS} mt-1 w-full font-mono`}
            />
          </FormField>
          <FormField label="Keyword arguments (a JSON object, optional)">
            <input
              value={kwargs}
              onChange={(e) => setKwargs(e.target.value)}
              placeholder="{}"
              className={`${INPUT_CLASS} mt-1 w-full font-mono`}
            />
          </FormField>
        </>
      )}
      {change.kind === 'rate_limit' && (
        <FormField label="Limit" hint="Runs per second, or 10/s, 100/m, 500/h. 0 lifts it.">
          <input
            value={rate}
            onChange={(e) => setRate(e.target.value.trim())}
            placeholder="10/m"
            className={`${INPUT_CLASS} mt-1 w-full font-mono`}
          />
        </FormField>
      )}
    </FormDialog>
  );
};

export type MenuItem =
  | { label: string; icon: React.ReactNode; to: string }
  | { label: string; icon: React.ReactNode; change: WorkerChange; danger?: boolean };

/** The dotted menu at the end of a row. Anything that changes something confirms first. */
export const RowMenu: React.FC<{ name: string; env: WorkerEnv; items: MenuItem[] }> = ({
  name,
  env,
  items,
}) => {
  const navigate = useNavigate();
  const [change, setChange] = useState<WorkerChange | null>(null);
  const links = items.filter((i) => 'to' in i);
  const changes = items.filter((i) => 'change' in i);
  if (!items.length) return null;
  return (
    <>
      {/* Not modal: a modal menu handing over to a dialog can leave the page unclickable. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label={`Actions for ${name}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="truncate">{name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {links.map(
            (i) =>
              'to' in i && (
                <DropdownMenuItem
                  key={i.label}
                  className="cursor-pointer gap-2"
                  onSelect={() => navigate(i.to)}
                >
                  {i.icon} {i.label}
                </DropdownMenuItem>
              ),
          )}
          {links.length > 0 && changes.length > 0 && <DropdownMenuSeparator />}
          {changes.map(
            (i) =>
              'change' in i && (
                <DropdownMenuItem
                  key={i.label}
                  className={`cursor-pointer gap-2 ${i.danger ? 'text-red-600 focus:text-red-700' : ''}`}
                  onSelect={() => setChange(i.change)}
                >
                  {i.icon} {i.label}
                </DropdownMenuItem>
              ),
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {change && <ChangeDialog env={env} change={change} onClose={() => setChange(null)} />}
    </>
  );
};

const ICON = 'h-4 w-4';

export const workerItems = (
  w: Worker,
  env: WorkerEnv,
  p: { canOperate: boolean },
  detail = true,
): MenuItem[] => [
  ...(detail ? [{ label: 'View details', icon: <Eye className={ICON} />, to: workerPath(w.name, env) }] : []),
  ...(p.canOperate && w.component
    ? [
        {
          label: 'Restart',
          icon: <RotateCw className={ICON} />,
          change: { kind: 'restart', component: w.component, worker: w.name } as WorkerChange,
          danger: true,
        },
      ]
    : []),
];

export const taskItems = (
  name: string,
  env: WorkerEnv,
  p: { canOperate: boolean; canManage: boolean },
  runnable: boolean,
  detail = true,
): MenuItem[] => [
  ...(detail ? [{ label: 'View details', icon: <Eye className={ICON} />, to: taskPath(name, env) }] : []),
  ...(p.canOperate && runnable
    ? [
        {
          label: 'Run now',
          icon: <Play className={ICON} />,
          change: { kind: 'run_task', task: name } as WorkerChange,
        },
      ]
    : []),
  ...(p.canManage
    ? [
        {
          label: 'Rate limit',
          icon: <Gauge className={ICON} />,
          change: { kind: 'rate_limit', task: name, rate: '' } as WorkerChange,
        },
      ]
    : []),
];

export const queueItems = (
  name: string,
  env: WorkerEnv,
  p: { canManage: boolean },
  depth: number,
  detail = true,
): MenuItem[] => [
  ...(detail ? [{ label: 'View messages', icon: <Eye className={ICON} />, to: queuePath(name, env) }] : []),
  ...(p.canManage && depth > 0
    ? [
        {
          label: 'Purge',
          icon: <Trash2 className={ICON} />,
          change: { kind: 'purge', queue: name } as WorkerChange,
          danger: true,
        },
      ]
    : []),
];

/** Pause or resume one queue on one worker. */
export const consumeItem = (worker: string, queue: string, consuming: boolean): MenuItem =>
  consuming
    ? {
        label: `Pause ${queue}`,
        icon: <Pause className={ICON} />,
        change: { kind: 'pause_queue', worker, queue },
        danger: true,
      }
    : {
        label: `Resume ${queue}`,
        icon: <Play className={ICON} />,
        change: { kind: 'resume_queue', worker, queue },
      };

/* ---------- tables used on more than one page ---------- */

const STATE_STYLE: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700',
  reserved: 'bg-gray-100 text-gray-600',
  scheduled: 'bg-violet-50 text-violet-700',
  SUCCESS: 'bg-green-50 text-green-700',
  FAILURE: 'bg-red-50 text-red-700',
  RETRY: 'bg-amber-50 text-amber-800',
};
const STATE_LABEL: Record<string, string> = {
  active: 'Running',
  reserved: 'Held, not started',
  scheduled: 'Waiting for its time',
  SUCCESS: 'Succeeded',
  FAILURE: 'Failed',
  RETRY: 'Retrying',
};

export const StateBadge: React.FC<{ state: string }> = ({ state }) => (
  <span
    className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${STATE_STYLE[state] ?? 'bg-gray-100 text-gray-600'}`}
  >
    {STATE_LABEL[state] ?? state}
  </span>
);

const Args: React.FC<{ args?: string | null; kwargs?: string | null }> = ({ args, kwargs }) =>
  args || kwargs ? (
    <span className="block max-w-md truncate font-mono text-[11px] text-gray-500" title={`${args ?? ''} ${kwargs ?? ''}`}>
      {args} {kwargs}
    </span>
  ) : null;

/** What workers hold right now: running, held and waiting for an eta. */
export const HeldTable: React.FC<{
  rows: HeldTask[];
  env: WorkerEnv;
  canOperate: boolean;
  loading?: boolean;
  showWorker?: boolean;
  empty?: string;
}> = ({ rows, env, canOperate, loading, showWorker = true, empty = 'Nothing is running or held.' }) => {
  const now = Date.now() / 1000;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
          <tr>
            <Th>Task</Th>
            <Th>State</Th>
            {showWorker && <Th>Worker</Th>}
            <Th>Queue</Th>
            <Th right>Running for</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <EmptyRow cols={showWorker ? 6 : 5} loading={loading}>
              {empty}
            </EmptyRow>
          )}
          {rows.map((t) => (
            <tr key={`${t.id}-${t.state}`} className="border-b border-gray-50 align-top last:border-0">
              <td className="px-4 py-2">
                <TaskLink name={t.name} env={env} />
                <Args args={t.args} kwargs={t.kwargs} />
              </td>
              <td className="px-4 py-2">
                <StateBadge state={t.state} />
                {t.eta && <span className="block text-[11px] text-gray-500">due {new Date(t.eta).toLocaleString()}</span>}
              </td>
              {showWorker && (
                <td className="px-4 py-2">
                  <WorkerLink name={t.worker} env={env} />
                </td>
              )}
              <td className="px-4 py-2">
                <QueueLink name={t.queue} env={env} />
              </td>
              <td className="px-4 py-2 text-right text-gray-700">
                {t.state === 'active' && t.started_at ? fmtSpan(now - t.started_at) : ''}
              </td>
              <td className="w-12 px-4 py-2 text-right">
                <RowMenu
                  name={shortTask(t.name)}
                  env={env}
                  items={[
                    { label: 'View task', icon: <Eye className={ICON} />, to: taskPath(t.name, env) },
                    ...(canOperate && t.state !== 'active'
                      ? [
                          {
                            label: 'Drop before it starts',
                            icon: <Ban className={ICON} />,
                            change: { kind: 'revoke', taskId: t.id, task: t.name } as WorkerChange,
                            danger: true,
                          },
                        ]
                      : []),
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Trace: React.FC<{ text?: string | null }> = ({ text }) =>
  text ? (
    <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-gray-950 p-3 font-mono text-xs leading-relaxed text-gray-100">
      {text}
    </pre>
  ) : null;

/** Finished runs, newest first. A failed one opens to its traceback. */
export const RunsTable: React.FC<{
  rows: Run[];
  env: WorkerEnv;
  loading?: boolean;
  showTask?: boolean;
}> = ({ rows, env, loading, showTask = true }) => {
  const [open, setOpen] = useState<string | null>(null);
  const cols = showTask ? 7 : 6;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
          <tr>
            <Th>Finished</Th>
            {showTask && <Th>Task</Th>}
            <Th>Outcome</Th>
            <Th>Worker</Th>
            <Th right>Waited</Th>
            <Th right>Ran for</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <EmptyRow cols={cols} loading={loading}>
              No runs kept for this filter. Results are kept for about a day.
            </EmptyRow>
          )}
          {rows.map((r) => (
            <React.Fragment key={r.id}>
              <tr className="border-b border-gray-50 align-top last:border-0">
                <td className="whitespace-nowrap px-4 py-2 text-gray-500">{fmtTime(r.at)}</td>
                {showTask && (
                  <td className="px-4 py-2">
                    <TaskLink name={r.task} env={env} />
                  </td>
                )}
                <td className="px-4 py-2">
                  <StateBadge state={r.status} />
                  {!!r.retries && <span className="ml-1 text-[11px] text-gray-500">try {r.retries + 1}</span>}
                </td>
                <td className="px-4 py-2">
                  <WorkerLink name={r.worker} env={env} />
                </td>
                <td className="px-4 py-2 text-right text-gray-700">{fmtMs(r.wait_ms)}</td>
                <td className="px-4 py-2 text-right text-gray-700">{fmtMs(r.runtime_ms)}</td>
                <td className="px-4 py-2 text-right">
                  {(r.traceback || r.result) && (
                    <Button size="sm" variant="ghost" onClick={() => setOpen(open === r.id ? null : r.id)}>
                      {open === r.id ? 'Hide' : r.traceback ? 'Traceback' : 'Result'}
                    </Button>
                  )}
                </td>
              </tr>
              {open === r.id && (
                <tr className="border-b border-gray-50">
                  <td colSpan={cols} className="px-4 pb-3">
                    <p className="font-mono text-[11px] text-gray-500">task id {r.id}</p>
                    <Trace text={r.traceback ?? r.result} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Failed runs with their tracebacks, each one sendable again when its arguments were kept. */
export const FailureList: React.FC<{
  rows: Failure[];
  env: WorkerEnv;
  canOperate: boolean;
  showTask?: boolean;
  empty?: string;
}> = ({ rows, env, canOperate, showTask = true, empty = 'No failures in this window.' }) => {
  const [open, setOpen] = useState<string | null>(null);
  if (!rows.length) return <p className="px-4 py-8 text-center text-sm text-gray-400">{empty}</p>;
  return (
    <ul className="divide-y divide-gray-50">
      {rows.map((f) => (
        <li key={f.id} className="px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-900">
                <span className="font-medium text-red-700">{f.exc_type}</span>{' '}
                <span className="break-words">{f.message}</span>
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
                <span>{fmtTime(f.at)}</span>
                {showTask && <Link to={taskPath(f.task, env)} className="hover:underline">{shortTask(f.task)}</Link>}
                {f.worker && <Link to={workerPath(f.worker, env)} className="hover:underline">{shortWorker(f.worker)}</Link>}
                {!!f.retries && <span>try {f.retries + 1}</span>}
              </p>
              <Args args={f.args} kwargs={f.kwargs} />
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === f.id ? null : f.id)}>
                {open === f.id ? 'Hide' : 'Traceback'}
              </Button>
              <RowMenu
                name={shortTask(f.task)}
                env={env}
                items={[
                  { label: 'View task', icon: <Eye className={ICON} />, to: taskPath(f.task, env) },
                  ...(canOperate && f.can_retry
                    ? [
                        {
                          label: 'Send again',
                          icon: <RotateCw className={ICON} />,
                          change: { kind: 'retry_failure', failureId: f.id } as WorkerChange,
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </div>
          {open === f.id && (
            <>
              <p className="mt-2 font-mono text-[11px] text-gray-500">
                task id {f.task_id ?? 'not recorded'}
                {!f.can_retry && '. Arguments were too large to keep, so this run cannot be sent again from here.'}
              </p>
              <Trace text={f.traceback ?? 'No traceback was recorded.'} />
            </>
          )}
        </li>
      ))}
    </ul>
  );
};

/* ---------- acknowledging ---------- */

/** The button and dialog that mark an issue or a failure group as seen. */
export const AckButton: React.FC<{
  issueId: string;
  name: string;
  env: WorkerEnv;
  standing?: boolean;
}> = ({ issueId, name, env, standing }) => {
  const [open, setOpen] = useState(false);
  const ack = useWorkerAckMutation();
  const close = () => {
    setOpen(false);
    ack.reset();
  };
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Acknowledge
      </Button>
      {open && (
        <ReasonDialog
          open
          onClose={close}
          title={`Acknowledge: ${name}`}
          description={
            standing
              ? 'Marks this as seen for a day. It re-opens if the condition is still there.'
              : 'Marks this as seen. A new occurrence re-opens it.'
          }
          confirmLabel="Acknowledge"
          busy={ack.isPending}
          error={ack.error}
          onConfirm={(note) =>
            ack.mutate({ issueId, note: note || undefined, name, env }, { onSuccess: close })
          }
        />
      )}
    </>
  );
};

const NEXT_STEP: Record<string, string> = {
  not_answering:
    'Open the worker and read its log: a crash loop shows there. If it is simply gone, restart it.',
  not_in_spec: 'Something outside the deployed spec is consuming these queues. Find out what started it.',
  not_consuming: 'Open the worker and resume the queue, or restart it to return to its deployed queues.',
  stuck: 'Read the worker log for what the task is waiting on. Only a restart frees the slot.',
  saturated: 'Work arrives faster than it finishes. Look for a slow task, or scale the component in Cloud.',
  failing: 'Open Failures to see what is breaking.',
  memory_high: 'The container is close to its limit and will be killed at it. Restart to recover, resize in Cloud to fix.',
  cpu_high: 'Check what is running; a tight loop shows as one task that never finishes.',
  restarts: 'Read the worker log from before the restart. Out-of-memory kills leave no traceback.',
  no_leader: 'No scheduler is running. Restart the beat component.',
  quiet: 'The scheduler holds its lock but dispatches nothing. Read its log, then restart it.',
  entries_overdue: 'Open Schedule to see which entries, and dispatch them by hand if they matter now.',
  unconsumed: 'No worker lists this queue. Its messages wait until one does: check the deployed spec.',
  no_consumer: 'Its worker has stopped consuming it. Resume the queue on that worker.',
  backlog: 'Open the queue to see what it is made of.',
  waiting: 'Open the queue to see what is at the front, and the worker that should be taking it.',
  unregistered:
    'A worker consuming this queue never imported the task and will drop it. Add the module to the worker includes and release.',
};

export const issuePath = (i: WorkerIssue): string =>
  i.subject.kind === 'worker'
    ? workerPath(i.subject.name, i.subject.env)
    : i.subject.kind === 'queue'
      ? queuePath(i.subject.name, i.subject.env)
      : i.subject.kind === 'task'
        ? taskPath(i.subject.name, i.subject.env)
        : tabPath('schedule', i.subject.env);

/** One reason something is not green: what it is, what to do next, and the way in. */
export const IssueRow: React.FC<{ issue: WorkerIssue; canOperate: boolean }> = ({
  issue,
  canOperate,
}) => (
  <div
    className={`rounded-xl border border-l-4 border-gray-100 bg-white p-4 shadow-sm ${
      HEALTH[issue.status === 'open' ? issue.level : 'grey'].border
    }`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <HealthPill level={issue.level} />
          <Link to={issuePath(issue)} className={LINK}>
            {issue.subject.kind === 'beat' ? 'Scheduler' : shortWorker(issue.subject.name)}
          </Link>
          <span className="text-xs text-gray-500">{issue.subject.kind}</span>
          {issue.status !== 'open' && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              acknowledged
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-800">{issue.text}</p>
        {NEXT_STEP[issue.code] && <p className="mt-0.5 text-sm text-gray-600">{NEXT_STEP[issue.code]}</p>}
        <p className="mt-0.5 text-xs text-gray-500">
          {issue.at ? `Since ${fmtTime(issue.at)}` : 'Ongoing'}
          {issue.ack
            ? `. Acknowledged by ${issue.ack.by} on ${fmtTime(issue.ack.at)}${issue.ack.note ? `: ${issue.ack.note}` : ''}`
            : ''}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" asChild>
          <Link to={issuePath(issue)}>Open</Link>
        </Button>
        {canOperate && issue.status === 'open' && (
          <AckButton
            issueId={issue.id}
            name={shortWorker(issue.subject.name)}
            env={issue.subject.env}
            standing={!issue.at}
          />
        )}
      </div>
    </div>
  </div>
);

/** One group of failures that are the same failure. */
export const FailureGroupRow: React.FC<{
  group: FailureGroup;
  env: WorkerEnv;
  canOperate: boolean;
  onOpen?: () => void;
  open?: boolean;
  children?: React.ReactNode;
}> = ({ group: g, env, canOperate, onOpen, open, children }) => (
  <div
    className={`overflow-hidden rounded-xl border border-l-4 border-gray-100 bg-white shadow-sm ${
      HEALTH[g.status === 'open' ? 'red' : 'grey'].border
    }`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3 p-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
            {fmtNum(g.count)} time{g.count === 1 ? '' : 's'}
          </span>
          <Link to={taskPath(g.task, env)} className={LINK} title={g.task}>
            {shortTask(g.task)}
          </Link>
          {g.status !== 'open' && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              acknowledged
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-800">
          <span className="font-medium text-red-700">{g.exc_type}</span>{' '}
          <span className="break-words">{g.message}</span>
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          Last {fmtAgo(g.last_at)}, first {fmtTime(g.first_at)}, on{' '}
          {g.workers.map(shortWorker).join(', ') || 'an unknown worker'}
          {g.ack ? `. Acknowledged by ${g.ack.by} on ${fmtTime(g.ack.at)}${g.ack.note ? `: ${g.ack.note}` : ''}` : ''}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onOpen && (
          <Button size="sm" variant="outline" onClick={onOpen}>
            {open ? 'Hide runs' : 'Show runs'}
          </Button>
        )}
        {canOperate && g.status === 'open' && (
          <AckButton issueId={`failure:${g.sig}`} name={shortTask(g.task)} env={env} />
        )}
      </div>
    </div>
    {children}
  </div>
);

/* ---------- audit ---------- */

const ACTION_LABEL: Record<string, string> = {
  run_task: 'Ran a task',
  run_entry: 'Dispatched a schedule entry',
  revoke: 'Dropped a task',
  retry_failure: 'Sent a failed run again',
  pause_queue: 'Paused a queue on a worker',
  resume_queue: 'Resumed a queue on a worker',
  restart: 'Restarted a worker',
  rate_limit: 'Set a rate limit',
  purge: 'Purged a queue',
  ack: 'Acknowledged',
};

const auditDetail = (r: WorkerAuditRow) =>
  [
    r.name,
    r.queue && r.queue !== r.name ? r.queue : '',
    r.rate ? `limit ${r.rate}` : '',
    r.removed != null ? `${r.removed} removed` : '',
    r.task_id ? `task id ${r.task_id}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

export const AuditTable: React.FC<{ rows: WorkerAuditRow[]; loading?: boolean; empty: string }> = ({
  rows,
  loading,
  empty,
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
        <tr>
          <Th>When</Th>
          <Th>Who</Th>
          <Th>What</Th>
          <Th>Reason</Th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <EmptyRow cols={4} loading={loading}>
            {empty}
          </EmptyRow>
        )}
        {rows.map((r, i) => (
          <tr key={`${r.at}-${i}`} className="border-b border-gray-50 align-top last:border-0">
            <td className="whitespace-nowrap px-4 py-2 text-gray-500">{fmtTime(r.at)}</td>
            <td className="whitespace-nowrap px-4 py-2 text-gray-700">{r.by}</td>
            <td className="px-4 py-2 text-gray-900">
              {ACTION_LABEL[r.action] ?? r.action}
              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">{r.env}</span>
              <span className="block break-all text-xs text-gray-500">{auditDetail(r)}</span>
            </td>
            <td className="px-4 py-2 text-gray-600">{r.reason || 'none given'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** A card whose body is a figures table by task or by worker. */
export const FiguresCard: React.FC<{
  title: string;
  rows: (Figures & { name: string })[];
  env: WorkerEnv;
  by: 'task' | 'worker';
  empty: string;
}> = ({ title, rows, env, by, empty }) => (
  <Card title={title} flush>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-100 text-left text-xs text-gray-500">
          <tr>
            <Th>{by === 'task' ? 'Task' : 'Worker'}</Th>
            <Th right>Runs</Th>
            <Th right>Failed</Th>
            <Th right>Avg run</Th>
            <Th right>Longest</Th>
            <Th right>Avg wait</Th>
            <Th right>Last ran</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyRow cols={7}>{empty}</EmptyRow>}
          {rows.map((r) => (
            <tr key={r.name} className="border-b border-gray-50 last:border-0">
              <td className="px-4 py-2">
                {by === 'task' ? <TaskLink name={r.name} env={env} /> : <WorkerLink name={r.name} env={env} />}
              </td>
              <td className="px-4 py-2 text-right text-gray-900">{fmtNum(r.runs)}</td>
              <td className="px-4 py-2 text-right">
                <FailRate f={r} />
              </td>
              <td className="px-4 py-2 text-right text-gray-700">{fmtMs(r.runtime_avg_ms)}</td>
              <td className="px-4 py-2 text-right text-gray-700">{fmtMs(r.runtime_max_ms)}</td>
              <td className="px-4 py-2 text-right text-gray-700">{fmtMs(r.wait_avg_ms)}</td>
              <td className="whitespace-nowrap px-4 py-2 text-right text-gray-500">{fmtAgo(r.last_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);
